import {
  useCashDrawerStatus,
  useCashDrawerWorkstations,
  useCurrentCashDrawer,
} from './cash-drawers.query'
import {
  findPairedCashDrawerWorkstation,
  pairedCashDrawerMatchesLocation,
  useCashDrawerDevicePairing,
} from './cash-drawer-device'
import {
  cashDrawerQueriesHaveError,
  CASH_DRAWER_DEVICE_SETUP_MESSAGE,
} from './cash-drawers.preflight'
import type { OrganizationIdInput } from '@/lib/organization-scoping'
import type { CashDrawerIdInput } from './cash-drawers.types'

interface ManualTenderDrawerOptions {
  organizationId: OrganizationIdInput
  targetLocationId: CashDrawerIdInput
  canOperateCashDrawers: boolean
  enabled?: boolean
}

// Render-time guidance only. Physical handoffs still recheck admission through preflight.
export function useManualTenderDrawer({
  organizationId,
  targetLocationId,
  canOperateCashDrawers,
  enabled = true,
}: ManualTenderDrawerOptions) {
  const statusQuery = useCashDrawerStatus({ enabled })
  const trackingEnabled = statusQuery.data?.enabled === true && !statusQuery.isError
  const workstationsQuery = useCashDrawerWorkstations({
    enabled: enabled && canOperateCashDrawers && trackingEnabled,
  })
  const { workstationId, pairWorkstation } = useCashDrawerDevicePairing(organizationId)
  const activeWorkstations = (workstationsQuery.data ?? []).filter(
    (workstation) => workstation.is_active && workstation.location.is_active
  )
  const pairedWorkstation = findPairedCashDrawerWorkstation(
    activeWorkstations,
    workstationId,
    targetLocationId
  )
  const currentDrawerQuery = useCurrentCashDrawer(pairedWorkstation?.id, {
    enabled: enabled && canOperateCashDrawers && trackingEnabled && Boolean(pairedWorkstation),
  })
  const pairedWorkstationMatchesOrder = pairedCashDrawerMatchesLocation(
    pairedWorkstation,
    targetLocationId
  )
  const pairedDrawer = currentDrawerQuery.data?.session ?? null
  const resolvedDrawer = pairedWorkstationMatchesOrder ? pairedDrawer : null
  const isLoading =
    enabled &&
    (statusQuery.isLoading ||
      (trackingEnabled &&
        canOperateCashDrawers &&
        (workstationsQuery.isLoading ||
          (Boolean(pairedWorkstation) && currentDrawerQuery.isLoading))))
  const hasQueryError = cashDrawerQueriesHaveError(
    statusQuery,
    workstationsQuery,
    currentDrawerQuery
  )
  const pairingBlocker = !pairedWorkstation
    ? CASH_DRAWER_DEVICE_SETUP_MESSAGE
    : !pairedWorkstationMatchesOrder
      ? `This device uses ${pairedWorkstation.name} at ${pairedWorkstation.location.name}, which does not match this order’s location.`
      : !resolvedDrawer
        ? `Start the cash drawer for ${pairedWorkstation.name} before recording cash or check activity.`
        : null
  return {
    statusQuery,
    trackingEnabled,
    workstationsQuery,
    workstationId,
    pairWorkstation,
    activeWorkstations,
    pairedWorkstation,
    currentDrawerQuery,
    pairedWorkstationMatchesOrder,
    pairedDrawer,
    resolvedDrawer,
    isLoading,
    hasQueryError,
    pairingBlocker,
  }
}
